import { lazy, Suspense, useLayoutEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { PublicTransferPage } from '@/features/public-transfers/PublicTransferPage';
import { matchTransferRefPath } from '@/features/public-transfers/transferRefPath';
import { TRANSFER_PREVIEW_PATH } from '@/features/public-transfers/transferLayout';
import { matchPublicGuidePath } from '@/features/public-guides/guidePaths';
import { paintSkin } from '@/shared/lib/skin';

const PublicGuideRouter = lazy(() => import('@/features/public-guides/PublicGuideRouter'));
const TransferPagePreview = lazy(() =>
  import('@/features/public-transfers/TransferPagePreview').then((m) => ({ default: m.TransferPagePreview })),
);

/** /_transfer-preview, with or without a trailing slash. */
const TRANSFER_PREVIEW = new RegExp(`^${TRANSFER_PREVIEW_PATH.replace(/[-/]/g, '\\$&')}/*$`);

/**
 * The guest pages Amassa duplicates from the PHP public tree, at the PHP
 * URLs, in front of the app's route table:
 *
 *   /<ref>, /cs/<ref>                         transfer page — `^(cs/)?(\d{4,})$`
 *   /guide, /places, /tours, /gifts, /gift-list [/<slug>]
 *                                             guide pages — the router's
 *                                             public-page rule, minus /feedback
 *                                             (left on PHP)
 *   /p/<slug>, /p/_preview                    the tenant's own guest pages
 *                                             (Guest Portal ▸ Pages) and the
 *                                             editor's preview frame
 *   /_transfer-preview                        the transfer page editor's
 *                                             canvas (Guest Portal ▸ Transfer
 *                                             page): the sample booking with
 *                                             the unsaved layout, same origin
 *
 * (hub-php _root/index.php:5-63). Every other path renders `children`
 * untouched. A gate instead of routes because React Router ranks a dynamic
 * segment above `*`: a `/:ref` route would capture every unknown one-segment
 * path (`/dashbord`) that today reaches the Shell's NotFound. Here only the
 * paths the PHP router itself claims are taken.
 *
 * It wraps VersionNotice too, so a guest who keeps a page open over a deploy
 * is never told "Amassa has been updated" on an agent's page.
 */
export function PublicPagesGate({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();

  // Guest pages keep their own design; every other page wears the
  // interface (shared/lib/skin.ts).
  useLayoutEffect(() => {
    paintSkin();
  }, [pathname]);

  const ref = matchTransferRefPath(pathname);
  if (ref) return <PublicTransferPage key={ref} token={ref} />;

  if (TRANSFER_PREVIEW.test(pathname)) {
    return (
      <Suspense fallback={<div style={{ minHeight: '100dvh', background: '#f0f2f5' }} aria-busy="true" />}>
        <TransferPagePreview />
      </Suspense>
    );
  }

  const guide = matchPublicGuidePath(pathname);
  if (guide) {
    return (
      <Suspense fallback={<div style={{ minHeight: '100dvh', background: '#f8f9fb' }} aria-busy="true" />}>
        <PublicGuideRouter match={guide} />
      </Suspense>
    );
  }

  return <>{children}</>;
}
